import { create } from 'zustand'
import { persist } from 'zustand/middleware'

interface User {
  id: number
  email?: string
  displayName?: string
  avatarUrl?: string
  telegramLinked: boolean
  isAdmin?: boolean  // DEC-049: Admin access for GlobalSettings
  role?: 'user' | 'clinician' | 'admin'
}

// Minimal user info saved after logout for "Continue as..." UX
interface LastUser {
  email?: string
  displayName?: string
  avatarUrl?: string
  loginMethod: 'telegram' | 'google' | 'email'
}

interface AuthState {
  user: User | null
  accessToken: string | null
  refreshToken: string | null
  isAuthenticated: boolean
  lastUser: LastUser | null // Persists after logout for quick re-login

  // Actions
  login: (user: User, accessToken: string, refreshToken: string, loginMethod?: 'telegram' | 'google' | 'email') => void
  logout: () => void
  updateTokens: (accessToken: string, refreshToken: string) => void
  updateUser: (user: Partial<User>) => void
  clearLastUser: () => void
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      lastUser: null,

      login: (user, accessToken, refreshToken, loginMethod = 'email') =>
        set({
          user,
          accessToken,
          refreshToken,
          isAuthenticated: true,
          // Save last user info for "Continue as..." UX
          lastUser: {
            email: user.email,
            displayName: user.displayName,
            avatarUrl: user.avatarUrl,
            loginMethod,
          },
        }),

      logout: () =>
        set({
          user: null,
          accessToken: null,
          refreshToken: null,
          isAuthenticated: false,
          // Keep lastUser for quick re-login UI
        }),

      updateTokens: (accessToken, refreshToken) =>
        set({ accessToken, refreshToken }),

      updateUser: (updates) =>
        set((state) => ({
          user: state.user ? { ...state.user, ...updates } : null,
        })),

      clearLastUser: () =>
        set({ lastUser: null }),
    }),
    {
      name: 'janus-auth',
      partialize: (state) => ({
        user: state.user,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
        lastUser: state.lastUser,
      }),
    }
  )
)
